import React, { useEffect, useState } from "react";
import { ApiService } from "services/ApiService";
import { useParams } from "react-router-dom";
import type { WorkflowTemplate } from "models/workflows/WorkflowTemplate";
import { summarizeCondition } from "utils/conditionSummary";

interface WorkflowViewerProps {
  workflowId: string;
  onEditClick: (template: WorkflowTemplate) => void;
}

export const WorkflowViewer: React.FC<WorkflowViewerProps> = ({ workflowId, onEditClick }) => {
  const { teamId } = useParams<{ teamId: string }>();
  const [template, setTemplate] = useState<WorkflowTemplate | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!teamId || !workflowId) return;
    setIsLoading(true);
    setError(null);
    ApiService.getWorkflowTemplate(teamId, workflowId)
      .then(setTemplate)
      .catch((err) => {
        console.error(err);
        setError("Failed to load workflow. Please try again.");
      })
      .finally(() => setIsLoading(false));
  }, [teamId, workflowId]);

  if (!teamId) return null;

  if (isLoading) {
    return (
      <div className="p-6 bg-white border border-gray-200 rounded-lg shadow-sm" aria-busy="true" aria-label="Loading workflow">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3" />
          <div className="h-4 bg-gray-200 rounded w-1/4" />
          <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(200px,1fr))]">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-white border border-red-200 rounded-lg shadow-sm" role="alert">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!template) return null;

  return (
    <div className="p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="mb-4">
        <h2 className="text-2xl font-semibold">{template.name}</h2>
        <p className="text-sm text-gray-600 mt-1">
          Status:{" "}
          <span className={template.isActive ? "text-green-600 font-medium" : "text-red-500 font-medium"}>
            {template.isActive ? "Live" : "Disabled"}
          </span>
        </p>
        {template.description && (
          <p className="text-sm text-gray-600 mt-1">
            Description: <span className="text-gray-800 font-medium">{template.description}</span>
          </p>
        )}
        {template.workflowLink && (
          <a
            href={template.workflowLink}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Open
          </a>
        )}
      </div>

      <button
        onClick={() => onEditClick(template)}
        className="mb-6 inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        Edit
      </button>

      {template.nodeTemplates.length === 0 ? (
        <p className="text-sm text-gray-500">No steps defined yet. Edit this workflow to add steps.</p>
      ) : (
        <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(200px,1fr))]">
          {[...template.nodeTemplates]
            .sort((a, b) => a.order - b.order)
            .map((nt) => (
              <div key={nt.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50 shadow-sm">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Step {nt.order}</p>
                <p className="font-semibold text-gray-800">{nt.nodeType}</p>
                <p className="text-sm text-gray-600">{nt.enrollmentType}</p>
              </div>
            ))}
        </div>
      )}

      {template.edgeTemplates.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-3">Edge Routing</h3>
          <div className="space-y-2">
            {[...template.edgeTemplates]
              .sort((a, b) => a.priority - b.priority)
              .map((edge) => {
                const fromNode = template.nodeTemplates.find((n) => n.id === edge.fromNodeTemplateId);
                const toNode = template.nodeTemplates.find((n) => n.id === edge.toNodeTemplateId);
                return (
                  <div key={edge.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50 shadow-sm">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-800">
                        Step {fromNode?.order ?? '?'} → Step {toNode?.order ?? '?'}
                      </span>
                      {edge.name && (
                        <span className="text-sm text-gray-600">({edge.name})</span>
                      )}
                      <span className="text-xs text-gray-400 ml-auto">Priority: {edge.priority}</span>
                    </div>
                    <span className={
                      summarizeCondition(edge.conditionJson) === 'Unconditional'
                        ? 'mt-1 block text-xs text-gray-400 italic'
                        : 'mt-1 block text-xs text-gray-700'
                    }>
                      {summarizeCondition(edge.conditionJson)}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}